import { Injectable, PLATFORM_ID, DOCUMENT, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { REQUEST_COOKIES } from '../../tokens/request-cookies.token';
import { BehaviorSubject, Observable } from 'rxjs';
import { CookieOptions, CookieService } from 'ngx-cookie';

import { StorageService } from '../storage/storage.service';

/**
 * Punto único de escritura del consentimiento.
 *
 * El formato de la cookie y la emisión de las señales de Consent Mode viven en
 * src/index.html (window.mcConsent), porque tienen que ejecutarse de forma
 * síncrona antes de que cargue GTM. Este servicio se ocupa de persistir la
 * decisión con los atributos correctos y de limpiar lo que deja de estar
 * consentido.
 *
 * Inyecta CookieService directamente y no UtilsService a propósito: UtilsService
 * va a consumir el consentimiento para decidir si carga los SDK de terceros, y
 * al revés habría un ciclo de dependencias.
 */
@Injectable({
  providedIn: 'root'
})
export class ConsentService {
  private cookies = inject(CookieService);
  private storage = inject(StorageService);
  private document = inject<Document>(DOCUMENT);
  private platformId = inject(PLATFORM_ID);
  private requestCookies = inject(REQUEST_COOKIES, { optional: true });


  static readonly COOKIE = 'mc_consent';
  /** Debe coincidir con VERSION en src/index.html. Subirla vuelve a preguntar a todo el mundo. */
  static readonly VERSION = 1;
  private static readonly MAX_AGE_DAYS = 180;

  /** Cookies de terceros que hay que barrer cuando su finalidad deja de estar consentida. */
  private static readonly THIRD_PARTY = {
    analytics: /^(_ga|_gid)/,
    ads: /^(_gcl_|_fbp|_fbc)/
  };

  private readonly state$ = new BehaviorSubject<McConsentState | null>(null);

  constructor() {
    this.state$.next(this.read());
  }

  get(): McConsentState | null {
    return this.read();
  }

  hasDecided(): boolean {
    // En servidor no existe window.mcConsent: se decide por la cookie de la
    // petición (server.ts la inyecta vía REQUEST_COOKIES). Así el banner de
    // cookies se renderiza en el SSR SOLO para quien no ha decidido, igual que
    // hará el cliente al hidratar (mismo HTML → sin mismatch ni parpadeo).
    // El formato v{VERSION}.ts.a.b.c es el de serialize() en src/index.html;
    // otra versión = se vuelve a preguntar, así que cuenta como "no decidido".
    if (!isPlatformBrowser(this.platformId)) {
      const re = new RegExp(`(^|;\\s*)${ConsentService.COOKIE}=v${ConsentService.VERSION}\\.\\d+\\.[01]\\.[01]\\.[01](;|$|\\s)`);
      return re.test(this.requestCookies || '');
    }
    return !!this.read();
  }

  has(purpose: keyof McConsentPrefs): boolean {
    const state = this.read();
    return !!state && !!state[purpose];
  }

  observe(): Observable<McConsentState | null> {
    return this.state$.asObservable();
  }

  acceptAll(): McConsentState | null {
    return this.set({ analytics: true, ads: true, personalization: true });
  }

  rejectAll(): McConsentState | null {
    return this.set({ analytics: false, ads: false, personalization: false });
  }

  /** Único camino por el que se guarda una decisión. */
  set(prefs: McConsentPrefs): McConsentState | null {
    const bridge = this.bridge();
    if (!bridge) return null;

    const state = bridge.update(prefs);
    this.cookies.put(ConsentService.COOKIE, bridge.serialize(state), this.cookieOptions());
    this.clearThirdPartyCookies(prefs);

    // El flag antiguo era un booleano sin finalidades, sin fecha y sin versión:
    // no se migra, sólo se limpia para que no quede rastro en localStorage.
    this.storage.deleteItem('accept_cookies').catch(() => {});

    this.state$.next(state);
    return state;
  }

  /**
   * Retira el consentimiento y recarga. La recarga es inevitable: fbevents.js y
   * el contenedor de GTM ya están en memoria y no se pueden descargar.
   */
  revoke(reload = true): void {
    if (!isPlatformBrowser(this.platformId)) return;

    this.cookies.remove(ConsentService.COOKIE, this.cookieOptions());
    this.clearThirdPartyCookies({ analytics: false, ads: false, personalization: false });
    if (this.document.defaultView?.mcConsent) this.document.defaultView.mcConsent.clear();
    this.state$.next(null);

    if (reload) this.document.defaultView?.location.reload();
  }

  private bridge(): McConsentBridge | null {
    if (!isPlatformBrowser(this.platformId)) return null;
    return this.document.defaultView?.mcConsent || null;
  }

  private read(): McConsentState | null {
    return this.bridge()?.get() || null;
  }

  private cookieOptions(): CookieOptions {
    const location = this.document.defaultView?.location;
    const hostname = location?.hostname || '';
    return {
      path: '/',
      expires: new Date(Date.now() + ConsentService.MAX_AGE_DAYS * 864e5),
      sameSite: 'lax',
      secure: location?.protocol === 'https:',
      // Apex y www comparten decisión; en localhost no se fija dominio.
      domain: /(^|\.)michollo\.com$/.test(hostname) ? '.michollo.com' : undefined,
      // El formato ya es URL-safe: así la cookie queda legible al depurar y
      // coincide byte a byte con lo que lee index.html.
      storeUnencoded: true,
    };
  }

  /**
   * Borra las cookies de las finalidades denegadas. Con el default en `denied`
   * ya no se crean, pero quien viene del estado anterior arrastra _ga y _fbp, y
   * sin este barrido seguirían ahí después de rechazar.
   */
  private clearThirdPartyCookies(prefs: McConsentPrefs): void {
    if (!isPlatformBrowser(this.platformId)) return;

    const patterns: RegExp[] = [];
    if (!prefs.analytics) patterns.push(ConsentService.THIRD_PARTY.analytics);
    if (!prefs.ads) patterns.push(ConsentService.THIRD_PARTY.ads);
    if (!patterns.length) return;

    const hostname = this.document.defaultView?.location.hostname || '';
    // Las de Google se escriben en el dominio superior, así que hay que probar
    // los dos ámbitos: host-only y punto delante.
    const domains: (string | undefined)[] = [undefined, hostname];
    if (/(^|\.)michollo\.com$/.test(hostname)) domains.push('.michollo.com');

    this.document.cookie.split(';').forEach(entry => {
      const name = entry.split('=')[0].trim();
      if (!name || !patterns.some(p => p.test(name))) return;
      domains.forEach(domain => {
        this.cookies.remove(name, domain ? { path: '/', domain } : { path: '/' });
      });
    });
  }
}
